import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { FolderPlus, UserPlus, FileText } from 'lucide-react';
import { getProjects } from '../api/projects';
import { getTasks } from '../api/tasks';
import { getAllUsers } from '../api/users';
import { Project } from '../types/project';
import { Task } from '../types/task';
import { User } from '../types/user';
import { calculateDashboardMetrics, generateIssueAlerts, calculateResourceMetrics, calculateTaskDistribution } from '../utils/dashboardUtils';
import { DashboardMetrics, IssueAlert, ResourceMetrics, TaskDistribution } from '../types/dashboard';
import DashboardMetricCard from '../components/DashboardMetricCard';
import IssueAlertCard from '../components/IssueAlertCard';
import Card from '../components/Card';
import Button from '../components/Button';
import ProgressBar from '../components/ProgressBar';
import Badge from '../components/Badge';
import { MetricCardSkeleton, CardSkeleton } from '../components/LoadingSkeleton';
import { NotificationBanner } from '../components/notifications/NotificationBanner';
import { useNotificationCounts } from '../hooks/notifications/useNotificationCounts';

const DashboardPage: React.FC = () => {
  const navigate = useNavigate();
  const [projects, setProjects] = useState<Project[]>([]);
  const [tasks, setTasks] = useState<Task[]>([]);
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [metrics, setMetrics] = useState<DashboardMetrics | null>(null);
  const [alerts, setAlerts] = useState<IssueAlert[]>([]);
  const [resourceMetrics, setResourceMetrics] = useState<ResourceMetrics | null>(null);
  const [taskDistribution, setTaskDistribution] = useState<TaskDistribution | null>(null);
  
  // Notification counts with 60 second auto-refresh
  const { counts: notificationCounts, error: notificationError, loading: notificationLoading } = useNotificationCounts(60000);
  
  // Debug logging for notifications
  useEffect(() => {
    console.log('[Dashboard] Notification counts:', notificationCounts);
    console.log('[Dashboard] Notification error:', notificationError);
    console.log('[Dashboard] Notification loading:', notificationLoading);
  }, [notificationCounts, notificationError, notificationLoading]);

  useEffect(() => {
    const loadDashboardData = async () => {
      setLoading(true);
      
      // Use Promise.allSettled to handle partial failures gracefully
      const results = await Promise.allSettled([
        getProjects(),
        getTasks(),
        getAllUsers()
      ]);

      const newErrors: Record<string, string> = {};

      // Process projects
      if (results[0].status === 'fulfilled') {
        setProjects(results[0].value);
      } else {
        newErrors.projects = results[0].reason instanceof Error 
          ? results[0].reason.message 
          : 'Failed to load projects';
      }

      // Process tasks
      if (results[1].status === 'fulfilled') {
        setTasks(results[1].value);
      } else {
        newErrors.tasks = results[1].reason instanceof Error 
          ? results[1].reason.message 
          : 'Failed to load tasks';
      }

      // Process users
      if (results[2].status === 'fulfilled') {
        setUsers(results[2].value);
      } else {
        newErrors.users = results[2].reason instanceof Error 
          ? results[2].reason.message 
          : 'Failed to load users';
      }

      setErrors(newErrors);
      setLoading(false);
    };

    loadDashboardData();
  }, []);

  // Calculate metrics whenever data changes
  useEffect(() => {
    if (!loading) {
      const calculatedMetrics = calculateDashboardMetrics(tasks, projects, users);
      setMetrics(calculatedMetrics);
      
      // Generate issue alerts from metrics
      const generatedAlerts = generateIssueAlerts(calculatedMetrics);
      setAlerts(generatedAlerts);
      
      // Calculate resource metrics
      const resourceStats = calculateResourceMetrics(users, tasks);
      setResourceMetrics(resourceStats);
      
      // Calculate task distribution
      const distribution = calculateTaskDistribution(tasks);
      setTaskDistribution(distribution);
    }
  }, [tasks, projects, users, loading]);

  // Loading state with skeletons
  if (loading) {
    return (
      <div className="max-w-7xl 2xl:max-w-[1600px] mx-auto px-3 sm:px-4 md:px-6 lg:px-8 xl:px-12 py-8">
        <div className="mb-8">
          <div className="h-10 w-64 bg-gray-200 dark:bg-gray-700 rounded-lg animate-pulse mb-2"></div>
          <div className="h-6 w-96 bg-gray-200 dark:bg-gray-700 rounded-lg animate-pulse"></div>
        </div>
        
        {/* Summary Metrics Skeleton */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <MetricCardSkeleton />
          <MetricCardSkeleton />
          <MetricCardSkeleton />
          <MetricCardSkeleton />
        </div>
        
        {/* Issues Section Skeleton */}
        <div className="mb-8">
          <CardSkeleton showFooter={false} />
        </div>
        
        {/* Additional Cards Skeleton */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <CardSkeleton showFooter={false} />
          <CardSkeleton showFooter={false} />
        </div>
      </div>
    );
  }

  // Empty state detection
  const isEmpty = metrics && metrics.total_projects === 0 && metrics.total_tasks === 0;

  if (isEmpty) {
    return (
      <div className="max-w-4xl mx-auto px-4 py-8">
        <Card variant="elevated">
          <Card.Header 
            title="Welcome to Project Management Dashboard" 
            subtitle="Get started by creating your first project"
          />
          <Card.Body>
            <div className="text-center py-8">
              <p className="text-lg text-gray-700 dark:text-gray-400 mb-6">
                You haven't created any projects or tasks yet. Start by setting up your workspace!
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
                <Link to="/projects">
                  <Button variant="primary" size="lg" leftIcon={FolderPlus}>
                    Create Your First Project
                  </Button>
                </Link>
                <Link to="/users">
                  <Button variant="secondary" size="lg" leftIcon={UserPlus}>
                    Add Team Members
                  </Button>
                </Link>
                <a 
                  href="https://github.com/AkhileDigital/project-management-frontend" 
                  target="_blank" 
                  rel="noopener noreferrer"
                >
                  <Button variant="outline" size="lg" leftIcon={FileText}>
                    View Documentation
                  </Button>
                </a>
              </div>
            </div>
          </Card.Body>
        </Card>
      </div>
    );
  }

  return (
    <div className="max-w-7xl 2xl:max-w-[1600px] mx-auto px-3 sm:px-4 md:px-6 lg:px-8 xl:px-12 py-8">
      <div className="mb-8">
        <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold bg-gradient-to-r from-primary-600 via-secondary-600 to-accent-600 bg-clip-text text-transparent">Project Management Dashboard</h1>
      </div>

      {/* Notification Banner */}
      {notificationError && (
        <div className="mb-6 p-4 rounded-lg bg-warning/10 dark:bg-warning/20 border border-warning/30 text-gray-700 dark:text-gray-300">
          <strong>Notification System Unavailable:</strong> {notificationError}
          <br />
          <span className="text-sm">You may still receive email notifications. This affects only the in-app notification banner.</span>
        </div>
      )}
      {notificationCounts && (notificationCounts.overdue_count > 0 || notificationCounts.upcoming_count > 0) && (
        <div className="mb-6">
          <NotificationBanner
            overdueCount={notificationCounts.overdue_count}
            upcomingCount={notificationCounts.upcoming_count}
          />
        </div>
      )}

      {/* Summary Metrics Section */}
      <section className="mb-8">
        <h2 className="text-xl font-semibold text-gray-800 dark:text-gray-200 mb-4">Summary Metrics</h2>
        {errors.projects || errors.tasks ? (
          <div className="rounded-lg p-4 bg-error/10 dark:bg-error/20 border border-error/30 text-error mb-4">
            Failed to load some metrics. {errors.projects || errors.tasks}
          </div>
        ) : null}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          <DashboardMetricCard
            title="Total Projects"
            value={metrics?.total_projects || 0}
            variant="default"
          />
          <DashboardMetricCard
            title="Total Tasks"
            value={metrics?.total_tasks || 0}
            variant="default"
          />
          <DashboardMetricCard
            title="Completion Rate"
            value={`${metrics?.completion_percentage || 0}%`}
            subtitle={`${metrics?.completed_tasks || 0} of ${metrics?.total_tasks || 0} tasks completed`}
            variant="success"
          />
          <DashboardMetricCard
            title="Overdue Tasks"
            value={metrics?.overdue_tasks || 0}
            variant="danger"
            onClick={() => navigate('/tasks', { state: { filterType: 'overdue', autoApply: true } })}
          />
        </div>
      </section>

      {/* Issues & Risks Section */}
      <section className="mb-8">
        <h2 className="text-xl font-semibold text-gray-800 dark:text-gray-200 mb-4">Issues & Risks</h2>
        {errors.tasks ? (
          <div className="rounded-lg p-4 bg-error/10 dark:bg-error/20 border border-error/30 text-error mb-4">
            Unable to load issues panel. {errors.tasks}
          </div>
        ) : null}
        {alerts.length === 0 ? (
          <Card variant="elevated">
            <Card.Body>
              <div className="text-center py-8">
                <div className="text-5xl mb-4">✅</div>
                <h3 className="text-xl font-semibold text-success mb-2">No critical issues detected</h3>
                <p className="text-gray-600 dark:text-gray-400">Everything is looking good! Keep up the great work.</p>
              </div>
            </Card.Body>
          </Card>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {alerts.map(alert => (
              <IssueAlertCard
                key={alert.id}
                alert={alert}
                onClick={() => navigate(alert.navigation_target, { state: alert.filter_state })}
              />
            ))}
          </div>
        )}
      </section>

      {/* Resource Capacity Section */}
      <section className="mb-8">
        <h2 className="text-xl font-semibold text-gray-800 dark:text-gray-200 mb-4">Resource Capacity</h2>
        {errors.users ? (
          <div className="rounded-lg p-4 bg-error/10 dark:bg-error/20 border border-error/30 text-error mb-4">
            Unable to load resource capacity. {errors.users}
          </div>
        ) : null}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 mb-6">
          <DashboardMetricCard
            title="Total Users"
            value={resourceMetrics?.total_users || 0}
            variant="default"
          />
          <DashboardMetricCard
            title="Active Users"
            value={resourceMetrics?.active_users || 0}
            subtitle={`${resourceMetrics?.average_tasks_per_user || 0} avg tasks/user`}
            variant="default"
          />
          <DashboardMetricCard
            title="Overloaded Users"
            value={metrics?.overloaded_users || 0}
            subtitle="More than 5 active tasks"
            variant="warning"
          />
        </div>

        {/* Top 3 Most Loaded Users */}
        {resourceMetrics && resourceMetrics.user_workload_list.length > 0 && (
          <Card variant="glass">
            <Card.Header title="User Workload Distribution" />
            <Card.Body>
              <div className="space-y-2">
                {resourceMetrics.user_workload_list
                  .filter(w => w.task_count > 0)
                  .sort((a, b) => b.task_count - a.task_count)
                  .slice(0, 10)
                  .map((workload, index) => {
                    const isTopThree = index < 3;
                    return (
                      <div 
                        key={workload.user_id} 
                        className="flex items-center justify-between py-2 border-b border-gray-200 dark:border-gray-700 last:border-0"
                      >
                        <span className={isTopThree ? 'font-bold text-lg dark:text-gray-100' : 'text-gray-700 dark:text-gray-300'}>
                          {workload.user_name}
                        </span>
                        <div className="flex items-center gap-3">
                          <span className={isTopThree ? 'font-semibold text-base dark:text-gray-100' : 'text-gray-600 dark:text-gray-400'}>
                            {workload.task_count} tasks
                          </span>
                          <Badge
                            variant={
                              workload.workload_status === 'overloaded'
                                ? 'danger'
                                : workload.workload_status === 'normal'
                                ? 'info'
                                : 'success'
                            }
                            size="sm"
                          >
                            {workload.workload_status}
                          </Badge>
                        </div>
                      </div>
                    );
                  })}
              </div>
            </Card.Body>
          </Card>
        )}
      </section>

      {/* Task Distribution Section */}
      <section className="mb-8">
        <h2 className="text-xl font-semibold text-gray-800 dark:text-gray-200 mb-4">Task Distribution</h2>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* By Status Card */}
          <Card variant="elevated">
            <Card.Header title="By Status" />
            <Card.Body>
              <div className="space-y-4">
                <div>
                  <div className="flex justify-between mb-2">
                    <span className="text-gray-700 dark:text-gray-300">To Do</span>
                    <span className="font-semibold text-gray-900 dark:text-gray-100">
                      {taskDistribution?.status_breakdown.todo || 0}
                    </span>
                  </div>
                  <ProgressBar
                    percentage={
                      metrics?.total_tasks
                        ? ((taskDistribution?.status_breakdown.todo || 0) / metrics.total_tasks) * 100
                        : 0
                    }
                    color="secondary"
                  />
                </div>

                <div>
                  <div className="flex justify-between mb-2">
                    <span className="text-gray-700 dark:text-gray-300">In Progress</span>
                    <span className="font-semibold text-gray-900 dark:text-gray-100">
                      {taskDistribution?.status_breakdown.in_progress || 0}
                    </span>
                  </div>
                  <ProgressBar
                    percentage={
                      metrics?.total_tasks
                        ? ((taskDistribution?.status_breakdown.in_progress || 0) / metrics.total_tasks) * 100
                        : 0
                    }
                    color="primary"
                  />
                </div>

                <div>
                  <div className="flex justify-between mb-2">
                    <span className="text-gray-700 dark:text-gray-300">Completed</span>
                    <span className="font-semibold text-gray-900 dark:text-gray-100">
                      {taskDistribution?.status_breakdown.completed || 0}
                    </span>
                  </div>
                  <ProgressBar
                    percentage={
                      metrics?.total_tasks
                        ? ((taskDistribution?.status_breakdown.completed || 0) / metrics.total_tasks) * 100
                        : 0
                    }
                    color="success"
                  />
                </div>
              </div>
            </Card.Body>
          </Card>

          {/* By Priority Card */}
          <Card variant="elevated">
            <Card.Header title="By Priority" />
            <Card.Body>
              <div className="space-y-4">
                <div>
                  <div className="flex justify-between mb-2">
                    <span className="text-gray-700 dark:text-gray-300">High</span>
                    <span className="font-semibold text-gray-900 dark:text-gray-100">
                      {taskDistribution?.priority_breakdown.high || 0}
                    </span>
                  </div>
                  <ProgressBar
                    percentage={
                      metrics?.total_tasks
                        ? ((taskDistribution?.priority_breakdown.high || 0) / metrics.total_tasks) * 100
                        : 0
                    }
                    color="danger"
                  />
                </div>

                <div>
                  <div className="flex justify-between mb-2">
                    <span className="text-gray-700 dark:text-gray-300">Medium</span>
                    <span className="font-semibold text-gray-900 dark:text-gray-100">
                      {taskDistribution?.priority_breakdown.medium || 0}
                    </span>
                  </div>
                  <ProgressBar
                    percentage={
                      metrics?.total_tasks
                        ? ((taskDistribution?.priority_breakdown.medium || 0) / metrics.total_tasks) * 100
                        : 0
                    }
                    color="warning"
                  />
                </div>

                <div>
                  <div className="flex justify-between mb-2">
                    <span className="text-gray-700 dark:text-gray-300">Low</span>
                    <span className="font-semibold text-gray-900 dark:text-gray-100">
                      {taskDistribution?.priority_breakdown.low || 0}
                    </span>
                  </div>
                  <ProgressBar
                    percentage={
                      metrics?.total_tasks
                        ? ((taskDistribution?.priority_breakdown.low || 0) / metrics.total_tasks) * 100
                        : 0
                    }
                    color="success"
                  />
                </div>
              </div>
            </Card.Body>
          </Card>
        </div>
      </section>
    </div>
  );
};

export default DashboardPage;
